import { useCallback, useEffect, useRef, useState } from "react";
import {
  checkDirectoryAccess,
  openAccessSettings,
  type DirectoryCheckResult,
} from "@/api/companion-local";
import { translate } from "@/i18n";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { useCompanionStore } from "@/stores/companion-store";

/** A successful local read is the only source of Allowed. */
export function useFolderAccess(path: string) {
  const port = useCompanionStore((state) => state.eventPort);
  const [access, setAccess] = useState<DirectoryCheckResult | null>(null);
  const [platform, setPlatform] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++revision.current;
    setAccess(null);
    setError(null);
    if (!port) {
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const result = await checkDirectoryAccess(port, [path]);
      if (id !== revision.current) return;
      setAccess(result.results[path] ?? null);
      setPlatform(result.platform);
    } catch {
      if (id === revision.current)
        setError(translate(settingsProjectsMessages, "checkAccessError"));
    } finally {
      if (id === revision.current) setChecking(false);
    }
  }, [path, port]);
  useEffect(() => {
    void refresh();
    const focus = () => void refresh();
    const visible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", visible);
    return () => {
      revision.current += 1;
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refresh]);
  async function grant() {
    if (!port) return;
    try {
      await openAccessSettings(port);
    } catch {
      setError(translate(settingsProjectsMessages, "openAccessSettingsError"));
    }
  }
  return {
    access,
    checking,
    error,
    refresh,
    grant,
    canGrant: Boolean(
      port && platform === "darwin" && access?.status === "blocked",
    ),
  };
}
